import { openDB } from 'idb';

import { MULTIPART_CONCURRENCY } from '../shared/contracts';

export type CompletedPart = { partNumber: number; etag: string };
export type UploadSession = {
  id: string;
  trackId: string;
  partSizeBytes: number;
  partCount: number;
  expiresAt: string;
};
export type UploadStatus = UploadSession & {
  filename: string;
  sizeBytes: number;
  completedParts: CompletedPart[];
};

export interface UploadApi {
  create(input: {
    filename: string;
    contentType: string;
    sizeBytes: number;
    lastModified: number;
  }): Promise<UploadSession>;
  status(id: string): Promise<UploadStatus>;
  signParts(
    id: string,
    partNumbers: number[],
  ): Promise<{
    expiresAt: string;
    parts: { partNumber: number; url: string }[];
  }>;
  complete(id: string, parts: CompletedPart[]): Promise<{ id: string }>;
  cancel(id: string): Promise<void>;
}

export interface UploadPersistence {
  get(fingerprint: string): Promise<{ fingerprint: string; uploadId: string } | null>;
  save(value: { fingerprint: string; uploadId: string }): Promise<void>;
  remove(fingerprint: string): Promise<void>;
}

export type UploadChunk = { readonly size: number };

type UploadOptions = {
  api: UploadApi;
  persistence: UploadPersistence;
  putPart?: (
    url: string,
    body: UploadChunk,
    partNumber: number,
    signal?: AbortSignal,
    onProgress?: (loaded: number) => void,
  ) => Promise<string>;
  sleep?: (milliseconds: number) => Promise<void>;
  signal?: AbortSignal;
  onProgress?: (progress: { uploadedBytes: number; totalBytes: number; percent: number }) => void;
};

type UploadFile = {
  readonly name: string;
  readonly size: number;
  readonly type: string;
  readonly lastModified: number;
  slice(start?: number, end?: number): UploadChunk;
};

export function fileFingerprint(file: UploadFile): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

export async function uploadMp3(
  file: UploadFile,
  options: UploadOptions,
): Promise<{ id: string }> {
  const fingerprint = fileFingerprint(file);
  const saved = await options.persistence.get(fingerprint);
  let session: UploadSession;
  let completedParts: CompletedPart[] = [];

  if (saved?.fingerprint === fingerprint) {
    try {
      const status = await options.api.status(saved.uploadId);
      session = status;
      completedParts = status.completedParts;
    } catch {
      await options.persistence.remove(fingerprint);
      session = await createSession(file, options.api);
    }
  } else {
    session = await createSession(file, options.api);
  }

  await options.persistence.save({ fingerprint, uploadId: session.id });
  const completed = new Map(completedParts.map((part) => [part.partNumber, part.etag]));
  const partProgress = new Map<number, number>();
  for (const partNumber of completed.keys()) {
    partProgress.set(partNumber, partLength(file.size, session.partSizeBytes, partNumber));
  }
  reportProgress(file.size, partProgress, options.onProgress);

  const missing = Array.from({ length: session.partCount }, (_, index) => index + 1).filter(
    (partNumber) => !completed.has(partNumber),
  );
  const putPart = options.putPart ?? putPartWithXhr;
  const sleep = options.sleep ?? ((milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)));

  try {
    for (let offset = 0; offset < missing.length; offset += 10) {
      const batch = missing.slice(offset, offset + 10);
      const signed = await options.api.signParts(session.id, batch);
      await runConcurrent(signed.parts, MULTIPART_CONCURRENCY, async ({ partNumber, url }) => {
        const start = (partNumber - 1) * session.partSizeBytes;
        const body = file.slice(start, Math.min(file.size, start + session.partSizeBytes));
        const etag = await retry(
          () =>
            putPart(url, body, partNumber, options.signal, (loaded) => {
              partProgress.set(partNumber, loaded);
              reportProgress(file.size, partProgress, options.onProgress);
            }),
          sleep,
          options.signal,
        );
        completed.set(partNumber, etag);
        partProgress.set(partNumber, body.size);
        reportProgress(file.size, partProgress, options.onProgress);
      });
    }

    const result = await options.api.complete(
      session.id,
      [...completed.entries()]
        .map(([partNumber, etag]) => ({ partNumber, etag }))
        .sort((a, b) => a.partNumber - b.partNumber),
    );
    await options.persistence.remove(fingerprint);
    return result;
  } catch (error) {
    if (options.signal?.aborted) {
      await options.api.cancel(session.id).catch(() => undefined);
      await options.persistence.remove(fingerprint);
    }
    throw error;
  }
}

async function createSession(file: UploadFile, api: UploadApi): Promise<UploadSession> {
  return api.create({
    filename: file.name,
    contentType: file.type,
    sizeBytes: file.size,
    lastModified: file.lastModified,
  });
}

function partLength(total: number, partSize: number, partNumber: number): number {
  const start = (partNumber - 1) * partSize;
  return Math.max(0, Math.min(partSize, total - start));
}

function reportProgress(
  totalBytes: number,
  parts: Map<number, number>,
  listener: UploadOptions['onProgress'],
): void {
  if (!listener) return;
  const uploadedBytes = [...parts.values()].reduce((sum, bytes) => sum + bytes, 0);
  listener({
    uploadedBytes,
    totalBytes,
    percent: totalBytes === 0 ? 0 : Math.min(100, Math.round((uploadedBytes / totalBytes) * 100)),
  });
}

async function retry<T>(
  operation: () => Promise<T>,
  sleep: (milliseconds: number) => Promise<void>,
  signal?: AbortSignal,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 5; attempt += 1) {
    if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (attempt < 4) await sleep(250 * 2 ** attempt);
    }
  }
  throw lastError;
}

async function runConcurrent<T>(
  values: T[],
  concurrency: number,
  worker: (value: T) => Promise<void>,
): Promise<void> {
  let nextIndex = 0;
  await Promise.all(
    Array.from({ length: Math.min(concurrency, values.length) }, async () => {
      for (;;) {
        const index = nextIndex;
        nextIndex += 1;
        const value = values[index];
        if (value === undefined) return;
        await worker(value);
      }
    }),
  );
}

export function putPartWithXhr(
  url: string,
  body: UploadChunk,
  _partNumber: number,
  signal?: AbortSignal,
  onProgress?: (loaded: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    request.upload.onprogress = (event) => onProgress?.(event.loaded);
    request.onerror = () => reject(new Error('Part upload failed'));
    request.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    request.onload = () => {
      if (request.status < 200 || request.status >= 300) {
        reject(new Error(`Part upload failed with status ${request.status}`));
        return;
      }
      const etag = request.getResponseHeader('ETag');
      if (!etag) {
        reject(new Error('R2 did not expose the uploaded part ETag'));
        return;
      }
      resolve(etag);
    };
    signal?.addEventListener('abort', () => request.abort(), { once: true });
    request.send(body as XMLHttpRequestBodyInit);
  });
}

export class IndexedDbUploadPersistence implements UploadPersistence {
  private readonly database = openDB('personal-soundcloud', 1, {
    upgrade(database) {
      database.createObjectStore('uploads', { keyPath: 'fingerprint' });
    },
  });

  async get(fingerprint: string) {
    return (await (await this.database).get('uploads', fingerprint)) ?? null;
  }

  async save(value: { fingerprint: string; uploadId: string }) {
    await (await this.database).put('uploads', value);
  }

  async remove(fingerprint: string) {
    await (await this.database).delete('uploads', fingerprint);
  }
}
